import { type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router';

import AccessDenied from '@/app/access-denied';
import { useRoleActionContext } from '@/context/role-actions';

import { useDelegation } from './delegation';
import { evaluateRoute } from './route-policy';

/**
 * `observe` computes the verdict and renders the page anyway; `enforce` denies.
 *
 * Unset reads as `observe`, which is the safe default during rollout — a build that
 * forgets the variable cannot lock anyone out. `vite.config.ts` inlines the whole
 * `process.env` object, so no per-variable registration is needed.
 *
 * NOTE this is baked at build time like every `REACT_APP_*` value here. Flipping it is
 * a rebuild, and rolling back is a redeploy.
 */
const MODE = process.env.REACT_APP_AUTHZ_ENFORCE === 'enforce' ? 'enforce' : 'observe';

/**
 * The single authorization gate for `/admin/*`.
 *
 * One guard resolving the pathname rather than 75 per-route wrappers, for two reasons:
 * wrappers cannot reach the routes declared inside `VesrLayout` and `ContactsLayout`,
 * and the "you can't forget one" property they buy is recovered more cheaply by the
 * exhaustiveness test in `route-policy.test.ts` — which also catches routes added later.
 *
 * Mounts inside `<Layout>` so a denial keeps the sidebar and header: someone who
 * mistyped a URL, or followed a colleague's link, should still be able to work.
 */
export const RouteGuard = ({ children }: { children: ReactNode }) => {
	const location = useLocation();
	const { snapshot } = useRoleActionContext();
	const delegation = useDelegation();

	const verdict = evaluateRoute(location.pathname, snapshot);
	const denied = !verdict.allowed;

	// Observe mode protects existing accounts from a lockout while the policy table settles. A team member
	// is a new kind of account whose access is an explicit grant list, so there is nothing to protect: the
	// menu already hides what they were not granted, and the guard must stop a typed URL too (otherwise a
	// member could open My Wallet, the owner's Team page or any module they were not given).
	const enforce = MODE === 'enforce' || delegation.isTeamMember;

	useEffect(() => {
		if (denied && !enforce) {
			// The production window that answers "who would we have locked out?" before
			// anyone's screen actually goes dark.
			console.warn(
				`[authz] would deny ${location.pathname}`,
				verdict.policy ? { needs: verdict.missing } : { reason: 'no policy claims this route' }
			);
		}
	}, [denied, enforce, location.pathname, verdict.missing, verdict.policy]);

	if (denied && enforce) {
		return <AccessDenied missing={verdict.missing} />;
	}

	return <>{children}</>;
};
